import { useTranslation } from "react-i18next";
import Modal from "react-bootstrap/esm/Modal";
import Button from "react-bootstrap/esm/Button";
import SundayService from "../DTOs/SundayService";

interface Props {
    service: (SundayService & { id: string }) | null;
    label: string;
    preacherName: (key: string) => string;
    slotModeLabel: (mode: string | undefined) => string;
    onClose: () => void;
}

/**
 * Read-only view of one service (GOD-53) – what members see when they open a
 * service; they may read everything but change nothing.
 */
export default function ServiceDetailModal({ service, label, preacherName, slotModeLabel, onClose }: Props) {
    const { t } = useTranslation();
    if (!service) return null;

    const slots = ([1, 2] as const)
        .map((slot) => ({
            slot,
            key: slot === 1 ? service.sermon1PreacherKey : service.sermon2PreacherKey,
            mode: slot === 1 ? service.sermon1Mode : service.sermon2Mode,
            passage: slot === 1 ? service.sermon1BiblePassage : service.sermon2BiblePassage,
            topic: slot === 1 ? service.sermon1Topic : service.sermon2Topic,
        }))
        .filter((s) => s.key || s.passage || s.topic);
    const songs = [service.choirSong1Name, service.choirSong2Name, service.choirSong3Name, service.choirSong4Name].filter(
        (name) => name && name.trim()
    );
    const prayer = [
        ...(service.prayerConcern?.trim() ? [service.prayerConcern.trim()] : []),
        ...(service.prayerConcerns ?? []).filter((c) => c && c.trim()),
    ];
    const notes = [
        service.guestPreacherOrigin && t("serviceDetail.guestFrom", { origin: service.guestPreacherOrigin }),
        service.afternoonEvent && t("serviceDetail.afternoon", { event: service.afternoonEvent }),
        service.choirInfo,
        service.serviceNote,
    ].filter(Boolean) as string[];
    const dateText = service.date.toLocaleDateString(t("calendar.locale") as string, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    return (
        <Modal show onHide={onClose} centered data-testid="service-detail">
            <Modal.Header closeButton>
                <Modal.Title as="div">
                    <div className="gl-meta">{dateText}</div>
                    <div className="h5 m-0">{label}</div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {slots.length === 0 && prayer.length === 0 && songs.length === 0 && notes.length === 0 && (
                    <p className="text-muted mb-0">{t("serviceDetail.empty")}</p>
                )}
                {slots.map((s) => (
                    <div key={s.slot} className="mb-3">
                        <div className="gl-meta mb-1">
                            {slotModeLabel(s.mode)} · {s.key ? preacherName(s.key) : t("serviceDetail.open")}
                        </div>
                        {s.passage && <div className="gl-mono">{s.passage}</div>}
                        {s.topic && <div className="text-muted">{s.topic}</div>}
                    </div>
                ))}
                {prayer.length > 0 && (
                    <div className="mb-3">
                        <div className="gl-meta mb-1">🙏 {t("serviceForm.prayerConcern")}</div>
                        <ul className="mb-0 ps-3">
                            {prayer.map((item, i) => (
                                <li key={i}>{item}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {songs.length > 0 && (
                    <div className="mb-3">
                        <div className="gl-meta mb-1">{t("serviceDetail.choirSongs")}</div>
                        <ol className="mb-0 ps-3">
                            {songs.map((song, i) => (
                                <li key={i}>{song}</li>
                            ))}
                        </ol>
                    </div>
                )}
                {notes.length > 0 && (
                    <div className="text-muted small">
                        {notes.map((note, i) => (
                            <div key={i}>{note}</div>
                        ))}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose}>
                    {t("serviceDetail.close")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
